/* eslint-disable */
// src/pages/ParentView.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { registerPushToken } from "../utils/push";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Sparkles,
} from "lucide-react";

function normStatus(value) {
  const s = String(value || "").trim().toLowerCase();
  if (!s) return "unknown";
  if (["present", "p"].includes(s)) return "present";
  if (["absent", "a"].includes(s)) return "absent";
  if (["late", "l"].includes(s)) return "late";
  if (["authorised", "authorized", "excused", "auth"].includes(s)) return "authorised";
  return "unknown";
}

function fmtPercent(n) {
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)}%`;
}

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function monthLabel(year, monthIndex) {
  return new Date(year, monthIndex, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function safeTsLabel(ts) {
  try {
    return ts?.toDate?.()?.toLocaleString?.() || "";
  } catch {
    return "";
  }
}

function safeDateLabel(row) {
  return row?.dateKey || row?.date || row?.id || "—";
}

function buildSummary(rows) {
  const counts = {
    present: 0,
    absent: 0,
    late: 0,
    authorised: 0,
    unknown: 0,
  };

  rows.forEach((row) => {
    counts[normStatus(row.status)] += 1;
  });

  const total = rows.length;
  const attendancePct =
    total > 0 ? ((counts.present + counts.late) / total) * 100 : NaN;

  return {
    total,
    counts,
    attendancePct,
  };
}

function getTodayCard(rows) {
  const today = todayIso();
  const row =
    rows.find((r) => String(r.dateKey || r.date || "") === today) ||
    rows[0] ||
    null;

  if (!row) return null;

  const s = normStatus(row.status);

  if (s === "present") {
    return {
      tone: "good",
      title: "Attendance marked",
      text: "Present today",
    };
  }

  if (s === "late") {
    return {
      tone: "warn",
      title: "Attendance marked",
      text: "Late today",
    };
  }

  if (s === "absent") {
    return {
      tone: "alert",
      title: "Attendance marked",
      text: "Absent today",
    };
  }

  if (s === "authorised") {
    return {
      tone: "info",
      title: "Attendance updated",
      text: "Authorised absence",
    };
  }

  return {
    tone: "info",
    title: "Attendance update",
    text: "No clear status yet",
  };
}

function getCellStyle(status) {
  if (status === "present") {
    return {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #bbf7d0",
    };
  }
  if (status === "absent") {
    return {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }
  if (status === "late") {
    return {
      background: "#fff7ed",
      color: "#b45309",
      border: "1px solid #fed7aa",
    };
  }
  if (status === "authorised") {
    return {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }
  return {
    background: "#fff",
    color: "#64748b",
    border: "1px solid #e5e7eb",
  };
}

function MonthlyAttendanceCalendar({
  attendanceRows = [],
  year,
  monthIndex,
}) {
  const map = useMemo(() => {
    const next = {};
    attendanceRows.forEach((row) => {
      const key = row.dateKey || row.date || row.id;
      if (key) next[key] = normStatus(row.status);
    });
    return next;
  }, [attendanceRows]);

  const cells = useMemo(() => {
    const first = new Date(year, monthIndex, 1);
    const last = new Date(year, monthIndex + 1, 0);
    const firstWeekday = (first.getDay() + 6) % 7;
    const totalDays = last.getDate();

    const out = [];

    for (let i = 0; i < firstWeekday; i += 1) {
      out.push({ empty: true, key: `empty-${i}` });
    }

    for (let d = 1; d <= totalDays; d += 1) {
      const y = year;
      const m = String(monthIndex + 1).padStart(2, "0");
      const day = String(d).padStart(2, "0");
      const dateKey = `${y}-${m}-${day}`;

      out.push({
        empty: false,
        key: dateKey,
        day: d,
        status: map[dateKey] || "unknown",
      });
    }

    return out;
  }, [year, monthIndex, map]);

  return (
    <div style={calendarWrap}>
      <div style={weekHeader}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} style={weekCell}>
            {d}
          </div>
        ))}
      </div>

      <div style={calendarGrid}>
        {cells.map((cell) =>
          cell.empty ? (
            <div key={cell.key} style={emptyCell} />
          ) : (
            <div
              key={cell.key}
              style={{
                ...calendarDayCell,
                ...getCellStyle(cell.status),
              }}
            >
              <div style={calendarDayNumber}>{cell.day}</div>
              <div style={calendarDayStatus}>{cell.status}</div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default function ParentView() {
  const { user, profile, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  const [school, setSchool] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeDays, setRangeDays] = useState(30);

  const now = new Date();
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());

  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [absenceSaving, setAbsenceSaving] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({
    fromDate: "",
    toDate: "",
    reason: "",
    note: "",
  });

  const schoolId = profile?.schoolId || null;
  const activeTab = searchParams.get("tab") || "home";

  const selectedChild = useMemo(
    () => children.find((c) => c.id === selectedChildId) || null,
    [children, selectedChildId]
  );

  useEffect(() => {
    if (user?.uid) {
      registerPushToken(user).catch(() => null);
    }
  }, [user]);

  useEffect(() => {
    let alive = true;

    async function loadParentPortal() {
      if (!user || !schoolId) {
        if (alive) setPageLoading(false);
        return;
      }

      setPageLoading(true);
      setError("");

      try {
        const [schoolSnap, userSnap] = await Promise.all([
          getDoc(doc(db, "schools", schoolId, "settings", "school")),
          getDoc(doc(db, "users", user.uid)),
        ]);

        if (!alive) return;

        setSchool(schoolSnap.exists() ? schoolSnap.data() : null);

        let linkedStudentIds = [];
        const userData = userSnap.exists() ? (userSnap.data() || {}) : {};

        if (Array.isArray(userData.children) && userData.children.length) {
          linkedStudentIds = userData.children;
        }

        let resolvedChildren = [];

        if (linkedStudentIds.length) {
          const childSnaps = await Promise.all(
            linkedStudentIds.map((id) =>
              getDoc(doc(db, "schools", schoolId, "students", id))
            )
          );

          resolvedChildren = childSnaps
            .filter((snap) => snap.exists())
            .map((snap) => ({ id: snap.id, ...snap.data() }));
        } else {
          const studentsSnap = await getDocs(
            collection(db, "schools", schoolId, "students")
          );

          resolvedChildren = studentsSnap.docs
            .map((snap) => ({ id: snap.id, ...snap.data() }))
            .filter((student) =>
              Array.isArray(student.parentAuthUids)
                ? student.parentAuthUids.includes(user.uid)
                : false
            );
        }

        resolvedChildren.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );

        if (!alive) return;

        setChildren(resolvedChildren);
        setSelectedChildId((prev) => {
          if (prev && resolvedChildren.some((c) => c.id === prev)) return prev;
          return resolvedChildren[0]?.id || "";
        });

        try {
          const announcementsSnap = await getDocs(
            query(
              collection(
                db,
                "schools",
                schoolId,
                "settings",
                "school",
                "announcements"
              ),
              orderBy("createdAt", "desc"),
              limit(10)
            )
          );

          if (!alive) return;

          setAnnouncements(
            announcementsSnap.docs.map((snap) => ({
              id: snap.id,
              ...snap.data(),
            }))
          );
        } catch {
          if (!alive) return;
          setAnnouncements([]);
        }
      } catch (err) {
        console.error("Parent portal load error:", err);
        if (!alive) return;
        setError("Unable to load parent portal.");
      } finally {
        if (alive) setPageLoading(false);
      }
    }

    loadParentPortal();

    return () => {
      alive = false;
    };
  }, [user, schoolId]);

  useEffect(() => {
    let alive = true;

    async function loadAttendance() {
      if (!schoolId || !selectedChildId) {
        if (alive) setAttendanceRows([]);
        return;
      }

      setAttendanceLoading(true);

      try {
        const snap = await getDocs(
          query(
            collection(
              db,
              "schools",
              schoolId,
              "students",
              selectedChildId,
              "attendance_daily"
            ),
            orderBy("dateKey", "desc"),
            limit(Math.max(31, Math.min(rangeDays, 120)))
          )
        );

        if (!alive) return;

        setAttendanceRows(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      } catch (err) {
        console.error("Attendance load error:", err);
        if (!alive) return;
        setAttendanceRows([]);
      } finally {
        if (alive) setAttendanceLoading(false);
      }
    }

    loadAttendance();

    return () => {
      alive = false;
    };
  }, [schoolId, selectedChildId, rangeDays]);

  const summary = useMemo(() => buildSummary(attendanceRows), [attendanceRows]);
  const todayCard = useMemo(() => getTodayCard(attendanceRows), [attendanceRows]);
  const latestAttendance = attendanceRows[0] || null;

  const handlePrevMonth = () => {
    const next = new Date(calendarYear, calendarMonth - 1, 1);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const handleNextMonth = () => {
    const next = new Date(calendarYear, calendarMonth + 1, 1);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const handleSubmitAbsence = async () => {
    if (!schoolId || !selectedChild || !user) return;

    if (!absenceForm.fromDate || !absenceForm.reason.trim()) {
      setError("Please add a start date and reason for the absence report.");
      return;
    }

    try {
      setAbsenceSaving(true);
      setError("");

      await addDoc(collection(db, "schools", schoolId, "absenceReports"), {
        schoolId,
        studentId: selectedChild.id,
        parentUid: user.uid,
        parentEmail: user.email || "",
        childName: selectedChild.name || "",
        fromDate: absenceForm.fromDate,
        toDate: absenceForm.toDate || absenceForm.fromDate,
        reason: absenceForm.reason.trim(),
        note: absenceForm.note.trim(),
        status: "submitted",
        submittedAt: serverTimestamp(),
      });

      setShowAbsenceModal(false);
      setAbsenceForm({
        fromDate: "",
        toDate: "",
        reason: "",
        note: "",
      });
    } catch (err) {
      console.error("Absence report failed:", err);
      setError("Unable to submit absence report.");
    } finally {
      setAbsenceSaving(false);
    }
  };

  if (authLoading || pageLoading) {
    return <div style={emptyState}>Loading parent app…</div>;
  }

  if (!user || !schoolId) {
    return <div style={emptyState}>Parent account is not linked to a school yet.</div>;
  }

  return (
    <div style={appShell}>
      <div style={page}>
        {error ? <div style={errorBanner}>{error}</div> : null}

        {children.length > 0 ? (
          <div style={childTabs}>
            {children.map((child) => {
              const active = selectedChildId === child.id;

              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => setSelectedChildId(child.id)}
                  style={active ? childTabActive(school?.primaryColor) : childTab}
                >
                  <div style={childAvatar(active, school?.primaryColor)}>
                    {String(child.name || "C").trim().charAt(0).toUpperCase()}
                  </div>

                  <div style={childTabText}>
                    <div style={childTabName}>{child.name || "Student"}</div>
                    <div style={childTabMeta}>
                      {child.className || child.classId || child.year || "Linked child"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        {activeTab === "home" && (
          <>
            <div style={feedHero(school?.primaryColor)}>
              <div style={feedHeroKicker}>Welcome back</div>
              <div style={feedHeroTitle}>{selectedChild?.name || "Your child"}’s day</div>
              <div style={feedHeroText}>
                School moments, attendance, and updates in one place.
              </div>

              {todayCard ? (
                <div style={todayBadge(todayCard.tone)}>
                  <span style={todayBadgeDot(todayCard.tone)} />
                  <span>{todayCard.text}</span>
                </div>
              ) : null}
            </div>

            <div style={actionGrid}>
              <button
                type="button"
                style={primaryAction(school?.primaryColor)}
                onClick={() => setShowAbsenceModal(true)}
              >
                Report absence
              </button>

              <button
                type="button"
                style={secondaryAction}
                onClick={() => window.scrollTo({ top: 760, behavior: "smooth" })}
              >
                Open feed
              </button>
            </div>

            <div style={summaryCards}>
              <SummaryCard
                title="Attendance"
                value={fmtPercent(summary.attendancePct)}
                color={school?.primaryColor}
                highlight
              />
              <SummaryCard title="Late" value={summary.counts.late} />
              <SummaryCard title="Present" value={summary.counts.present} />
              <SummaryCard title="Absent" value={summary.counts.absent} />
            </div>

            <div style={feedSectionHeader}>
              <div style={sectionIcon}>
                <Megaphone size={16} />
              </div>
              <div>
                <div style={sectionKicker}>Live feed</div>
                <div style={sectionHeading}>Today’s updates</div>
              </div>
            </div>

            <div style={feedList}>
              {todayCard ? (
                <div style={feedCard}>
                  <div style={feedCardHeader}>
                    <div style={feedAvatar(school?.primaryColor)}>📍</div>
                    <div>
                      <div style={feedTitle}>Attendance update</div>
                      <div style={feedMeta}>Today</div>
                    </div>
                  </div>
                  <div style={feedBody}>{todayCard.text}</div>
                  <div style={chip(todayCard.tone)}>
                    {todayCard.tone === "good"
                      ? "Present"
                      : todayCard.tone === "warn"
                      ? "Late"
                      : todayCard.tone === "alert"
                      ? "Absent"
                      : "Info"}
                  </div>
                </div>
              ) : null}

              {announcements.slice(0, 3).map((item) => (
                <div key={item.id} style={feedCard}>
                  <div style={feedCardHeader}>
                    <div style={feedAvatar("#1d4ed8")}>📣</div>
                    <div>
                      <div style={feedTitle}>{item.title || "School update"}</div>
                      <div style={feedMeta}>{safeTsLabel(item.createdAt) || "School"}</div>
                    </div>
                  </div>
                  <div style={feedBody}>
                    {item.message || item.body || "No message provided."}
                  </div>
                </div>
              ))}

              {latestAttendance ? (
                <div style={feedCard}>
                  <div style={feedCardHeader}>
                    <div style={feedAvatar("#047857")}>🗓️</div>
                    <div>
                      <div style={feedTitle}>Latest attendance record</div>
                      <div style={feedMeta}>{safeDateLabel(latestAttendance)}</div>
                    </div>
                  </div>
                  <div style={feedBody}>
                    Status recorded as <strong>{latestAttendance.status || "Unknown"}</strong>.
                  </div>
                  <div style={attendanceBadge(normStatus(latestAttendance.status))}>
                    {latestAttendance.status || "Unknown"}
                  </div>
                </div>
              ) : null}

              {!todayCard && !announcements.length && !latestAttendance ? (
                <div style={card}>
                  <div style={softNote}>No updates yet.</div>
                </div>
              ) : null}
            </div>
          </>
        )}

        {activeTab === "attendance" && (
          <>
            <div style={rangePills}>
              {[30, 60, 90].map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setRangeDays(days)}
                  style={rangeDays === days ? pillActive(school?.primaryColor) : pill}
                >
                  Last {days} days
                </button>
              ))}
            </div>

            <div style={card}>
              <div style={attendanceHeader}>
                <div>
                  <div style={sectionKicker}>Attendance</div>
                  <h3 style={cardTitle}>Monthly calendar</h3>
                  <p style={cardText}>
                    Visual attendance history for {selectedChild?.name || "your child"}.
                  </p>
                </div>

                <div style={monthNav}>
                  <button type="button" style={monthBtn} onClick={handlePrevMonth}>
                    <ChevronLeft size={18} />
                  </button>
                  <div style={monthLabelStyle}>
                    {monthLabel(calendarYear, calendarMonth)}
                  </div>
                  <button type="button" style={monthBtn} onClick={handleNextMonth}>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              <MonthlyAttendanceCalendar
                attendanceRows={attendanceRows}
                year={calendarYear}
                monthIndex={calendarMonth}
              />
            </div>

            <div style={card}>
              <div style={feedSectionHeader}>
                <div style={sectionIcon}>
                  <CalendarDays size={16} />
                </div>
                <div>
                  <div style={sectionKicker}>Records</div>
                  <div style={sectionHeading}>Attendance history</div>
                </div>
              </div>

              {attendanceLoading ? (
                <div style={softNote}>Loading attendance…</div>
              ) : attendanceRows.length ? (
                <div style={attendanceList}>
                  {attendanceRows.map((row) => (
                    <div key={row.id} style={attendanceRow}>
                      <div>
                        <div style={attendanceDate}>{safeDateLabel(row)}</div>
                        {row.note ? <div style={attendanceMeta}>{row.note}</div> : null}
                      </div>

                      <div style={attendanceBadge(normStatus(row.status))}>
                        {row.status || "Unknown"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={softNote}>No attendance history available.</div>
              )}
            </div>
          </>
        )}

        {activeTab === "updates" && (
          <div style={card}>
            <div style={feedSectionHeader}>
              <div style={sectionIcon}>
                <Sparkles size={16} />
              </div>
              <div>
                <div style={sectionKicker}>School feed</div>
                <div style={sectionHeading}>Updates and notices</div>
              </div>
            </div>

            {announcements.length ? (
              <div style={feedList}>
                {announcements.map((item) => (
                  <div key={item.id} style={feedCard}>
                    <div style={feedCardHeader}>
                      <div style={feedAvatar(school?.primaryColor)}>📣</div>
                      <div>
                        <div style={feedTitle}>{item.title || "School update"}</div>
                        <div style={feedMeta}>{safeTsLabel(item.createdAt) || "School"}</div>
                      </div>
                    </div>

                    <div style={feedBody}>
                      {item.message || item.body || "No message provided."}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={softNote}>No updates right now.</div>
            )}
          </div>
        )}
      </div>

      {showAbsenceModal ? (
        <div style={modalBackdrop} onClick={() => setShowAbsenceModal(false)}>
          <div style={modalSheet} onClick={(e) => e.stopPropagation()}>
            <div style={sheetGrabber} />
            <div style={modalTitle}>Report an absence</div>
            <div style={modalText}>
              Send an absence report for {selectedChild?.name || "your child"}.
            </div>

            <div style={modalField}>
              <label style={modalLabel}>From date</label>
              <input
                type="date"
                style={modalInput}
                value={absenceForm.fromDate}
                onChange={(e) =>
                  setAbsenceForm((p) => ({ ...p, fromDate: e.target.value }))
                }
              />
            </div>

            <div style={modalField}>
              <label style={modalLabel}>To date</label>
              <input
                type="date"
                style={modalInput}
                value={absenceForm.toDate}
                onChange={(e) =>
                  setAbsenceForm((p) => ({ ...p, toDate: e.target.value }))
                }
              />
            </div>

            <div style={modalField}>
              <label style={modalLabel}>Reason</label>
              <input
                type="text"
                placeholder="Medical appointment"
                style={modalInput}
                value={absenceForm.reason}
                onChange={(e) =>
                  setAbsenceForm((p) => ({ ...p, reason: e.target.value }))
                }
              />
            </div>

            <div style={modalField}>
              <label style={modalLabel}>Note</label>
              <textarea
                rows={3}
                placeholder="Add any helpful details"
                style={modalTextarea}
                value={absenceForm.note}
                onChange={(e) =>
                  setAbsenceForm((p) => ({ ...p, note: e.target.value }))
                }
              />
            </div>

            <div style={sheetActions}>
              <button
                type="button"
                style={sheetCancelBtn}
                onClick={() => setShowAbsenceModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                style={sheetSubmitBtn(school?.primaryColor)}
                onClick={handleSubmitAbsence}
                disabled={absenceSaving}
              >
                {absenceSaving ? "Submitting..." : "Submit report"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SummaryCard({ title, value, highlight = false, color = "#4F46E5" }) {
  return (
    <div
      style={{
        ...summaryCard,
        ...(highlight
          ? {
              background: `linear-gradient(135deg, ${color}, #6366f1)`,
              color: "#fff",
              boxShadow: "0 16px 36px rgba(79,70,229,0.20)",
            }
          : {}),
      }}
    >
      <div
        style={{
          ...summaryTitle,
          ...(highlight ? { color: "rgba(255,255,255,0.76)" } : {}),
        }}
      >
        {title}
      </div>
      <div style={summaryValue}>{value}</div>
    </div>
  );
}

const appShell = {
  minHeight: "100vh",
  background: "transparent",
};

const page = {
  width: "100%",
  maxWidth: 760,
  margin: "0 auto",
  padding: "10px 16px 28px",
  display: "grid",
  gap: 16,
};

const childTabs = {
  display: "flex",
  gap: 12,
  overflowX: "auto",
  paddingBottom: 4,
};

const childTab = {
  minWidth: 176,
  padding: "14px 16px",
  borderRadius: 24,
  border: "1px solid rgba(15,23,42,0.05)",
  background: "#ffffff",
  textAlign: "left",
  boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const childTabActive = (color) => ({
  ...childTab,
  background: `linear-gradient(135deg, ${color || "#4F46E5"} 0%, #6366f1 100%)`,
  color: "#fff",
  boxShadow: "0 18px 36px rgba(79,70,229,0.20)",
});

const childAvatar = (active, color) => ({
  width: 46,
  height: 46,
  borderRadius: 18,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  fontWeight: 900,
  fontSize: 18,
  background: active ? "rgba(255,255,255,0.16)" : `${color || "#4F46E5"}14`,
  color: active ? "#fff" : color || "#4F46E5",
});

const childTabText = {
  minWidth: 0,
};

const childTabName = {
  fontWeight: 900,
  color: "inherit",
  fontSize: 16,
};

const childTabMeta = {
  marginTop: 4,
  fontSize: 12,
  color: "inherit",
  opacity: 0.82,
};

const feedHero = (color) => ({
  borderRadius: 30,
  padding: 22,
  background: `linear-gradient(135deg, ${color || "#4F46E5"} 0%, #5b5cff 100%)`,
  color: "#fff",
  boxShadow: "0 24px 52px rgba(79,70,229,0.20)",
});

const feedHeroKicker = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  opacity: 0.84,
};

const feedHeroTitle = {
  marginTop: 10,
  fontSize: 30,
  lineHeight: 1.02,
  fontWeight: 900,
  letterSpacing: "-0.03em",
};

const feedHeroText = {
  marginTop: 8,
  fontSize: 14,
  lineHeight: 1.6,
  opacity: 0.94,
  maxWidth: 260,
};

const todayBadge = (tone) => ({
  marginTop: 16,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 800,
  background:
    tone === "alert"
      ? "rgba(239,68,68,0.18)"
      : tone === "warn"
      ? "rgba(245,158,11,0.20)"
      : tone === "good"
      ? "rgba(16,185,129,0.18)"
      : "rgba(59,130,246,0.18)",
});

const todayBadgeDot = (tone) => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  background:
    tone === "alert"
      ? "#fca5a5"
      : tone === "warn"
      ? "#fdba74"
      : tone === "good"
      ? "#86efac"
      : "#93c5fd",
});

const actionGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const primaryAction = (color) => ({
  width: "100%",
  minHeight: 54,
  borderRadius: 20,
  border: "none",
  background: color || "#4F46E5",
  color: "#fff",
  fontWeight: 900,
  fontSize: 15,
  boxShadow: "0 12px 28px rgba(79,70,229,0.18)",
});

const secondaryAction = {
  width: "100%",
  minHeight: 54,
  borderRadius: 20,
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  fontSize: 15,
};

const summaryCards = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

const summaryCard = {
  padding: 18,
  borderRadius: 24,
  background: "#fff",
  boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
};

const summaryTitle = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 800,
};

const summaryValue = {
  marginTop: 10,
  fontSize: 32,
  lineHeight: 1,
  fontWeight: 900,
};

const feedSectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const sectionIcon = {
  width: 34,
  height: 34,
  borderRadius: 12,
  background: "#eef2ff",
  color: "#4F46E5",
  display: "grid",
  placeItems: "center",
};

const sectionKicker = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "#6366f1",
};

const sectionHeading = {
  marginTop: 2,
  fontSize: 22,
  fontWeight: 900,
  color: "#0f172a",
};

const feedList = {
  display: "grid",
  gap: 12,
};

const feedCard = {
  background: "rgba(255,255,255,0.92)",
  borderRadius: 24,
  padding: 16,
  border: "1px solid rgba(255,255,255,0.72)",
  boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
  display: "grid",
  gap: 12,
};

const feedCardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const feedAvatar = (color) => ({
  width: 42,
  height: 42,
  minWidth: 42,
  borderRadius: 14,
  background: `${color || "#4F46E5"}18`,
  display: "grid",
  placeItems: "center",
  fontSize: 18,
});

const feedTitle = {
  fontSize: 15,
  fontWeight: 900,
  color: "#171c3a",
};

const feedMeta = {
  marginTop: 3,
  fontSize: 12,
  color: "#94a3b8",
};

const feedBody = {
  fontSize: 14,
  lineHeight: 1.65,
  color: "#475569",
};

const chip = (tone) => ({
  justifySelf: "flex-start",
  padding: "8px 12px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  background:
    tone === "alert"
      ? "#fef2f2"
      : tone === "warn"
      ? "#fff7ed"
      : tone === "good"
      ? "#ecfdf5"
      : "#eef2ff",
  color:
    tone === "alert"
      ? "#b91c1c"
      : tone === "warn"
      ? "#b45309"
      : tone === "good"
      ? "#047857"
      : "#4338ca",
});

const rangePills = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const pill = {
  padding: "10px 16px",
  borderRadius: 999,
  border: "1px solid rgba(15,23,42,0.08)",
  background: "#fff",
  color: "#334155",
  fontWeight: 800,
  fontSize: 13,
};

const pillActive = (color) => ({
  ...pill,
  background: color || "#4F46E5",
  color: "#fff",
  border: "1px solid transparent",
});

const card = {
  background: "rgba(255,255,255,0.88)",
  borderRadius: 28,
  padding: 20,
  border: "1px solid rgba(255,255,255,0.72)",
  boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
};

const cardTitle = {
  margin: 0,
  fontSize: 20,
  fontWeight: 900,
  color: "#171c3a",
};

const cardText = {
  marginTop: 8,
  fontSize: 14,
  color: "#667085",
  lineHeight: 1.6,
};

const attendanceHeader = {
  display: "grid",
  gap: 14,
};

const monthNav = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};

const monthBtn = {
  width: 42,
  height: 42,
  borderRadius: 14,
  border: "1px solid #dbe2ea",
  background: "#fff",
  display: "grid",
  placeItems: "center",
};

const monthLabelStyle = {
  flex: 1,
  textAlign: "center",
  fontWeight: 900,
  fontSize: 15,
  color: "#334155",
};

const calendarWrap = {
  display: "grid",
  gap: 10,
  marginTop: 16,
};

const weekHeader = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: 6,
};

const weekCell = {
  textAlign: "center",
  fontSize: 12,
  fontWeight: 800,
  color: "#64748b",
};

const calendarGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: 6,
};

const emptyCell = {
  minHeight: 66,
};

const calendarDayCell = {
  minHeight: 66,
  borderRadius: 16,
  padding: 8,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const calendarDayNumber = {
  fontWeight: 900,
  fontSize: 14,
};

const calendarDayStatus = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "capitalize",
};

const attendanceList = {
  display: "grid",
  gap: 10,
  marginTop: 14,
};

const attendanceRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  padding: 14,
  borderRadius: 18,
  background: "#fff",
  boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
};

const attendanceDate = {
  fontWeight: 900,
  color: "#171c3a",
  fontSize: 15,
};

const attendanceMeta = {
  marginTop: 4,
  fontSize: 13,
  color: "#64748b",
};

const attendanceBadge = (status) => ({
  padding: "9px 13px",
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 12,
  background:
    status === "present"
      ? "#ecfdf5"
      : status === "absent"
      ? "#fef2f2"
      : status === "late"
      ? "#fff7ed"
      : "#eff6ff",
  color:
    status === "present"
      ? "#047857"
      : status === "absent"
      ? "#b91c1c"
      : status === "late"
      ? "#b45309"
      : "#1d4ed8",
});

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.38)",
  display: "flex",
  alignItems: "flex-end",
  zIndex: 60,
};

const modalSheet = {
  width: "100%",
  background: "#fff",
  borderTopLeftRadius: 28,
  borderTopRightRadius: 28,
  padding: "12px 18px 24px",
  boxShadow: "0 -14px 34px rgba(15,23,42,0.16)",
  display: "grid",
  gap: 12,
};

const sheetGrabber = {
  width: 46,
  height: 5,
  borderRadius: 999,
  background: "#dbe2ea",
  justifySelf: "center",
};

const modalTitle = {
  fontSize: 22,
  fontWeight: 900,
  color: "#171c3a",
};

const modalText = {
  fontSize: 14,
  color: "#64748b",
  lineHeight: 1.6,
};

const modalField = {
  display: "grid",
  gap: 6,
};

const modalLabel = {
  fontSize: 13,
  fontWeight: 800,
  color: "#334155",
};

const modalInput = {
  width: "100%",
  boxSizing: "border-box",
  padding: "13px 14px",
  borderRadius: 14,
  border: "1px solid #dbe2ea",
  background: "#fff",
};

const modalTextarea = {
  width: "100%",
  boxSizing: "border-box",
  padding: "13px 14px",
  borderRadius: 14,
  border: "1px solid #dbe2ea",
  background: "#fff",
  resize: "vertical",
  fontFamily: "inherit",
};

const sheetActions = {
  display: "grid",
  gap: 10,
  marginTop: 6,
};

const sheetCancelBtn = {
  width: "100%",
  minHeight: 52,
  borderRadius: 18,
  border: "1px solid #dbe2ea",
  background: "#fff",
  color: "#334155",
  fontWeight: 800,
  fontSize: 15,
};

const sheetSubmitBtn = (color) => ({
  width: "100%",
  minHeight: 54,
  borderRadius: 18,
  border: "none",
  background: color || "#4F46E5",
  color: "#fff",
  fontWeight: 900,
  fontSize: 16,
});

const errorBanner = {
  padding: "12px 14px",
  borderRadius: 16,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
};

const softNote = {
  marginTop: 14,
  color: "#64748b",
  fontSize: 14,
};

const emptyState = {
  minHeight: "60vh",
  display: "grid",
  placeItems: "center",
  color: "#64748b",
  fontWeight: 700,
};